import axios from "axios";
import configuration from "../configuration/configuration";
import encodeDataToURL from "../components/Akitatek/encodeDatatoURL";

/**
 * Appels de la recherche universelle et de la recherche des lignes de facture (voir RechercheController côté API).
 * Une recherche par frappe : seule la réponse de la dernière compte (voir dernierAppel).
 */
export default class RechercheAPI {

    async chercher(q) {
        const response = await axios.get(`${configuration.API}/api/recherche?` + encodeDataToURL({q}));
        return response.data?.datas ?? {};
    }

    async lignes(q) {
        const response = await axios.get(`${configuration.API}/api/recherche/lignes?` + encodeDataToURL({q}));
        return response.data?.datas ?? {prestations: [], produits: []};
    }
}

/**
 * Enveloppe une fonction asynchrone pour que seule la réponse de son dernier appel soit rendue :
 * les réponses des appels dépassés (frappes précédentes) donnent undefined.
 */
export function dernierAppel(fonction) {
    let numero = 0;
    return async (...args) => {
        const moi = ++numero;
        const resultat = await fonction(...args);
        return moi === numero ? resultat : undefined;
    };
}

/** Minuscules sans accents, pour comparer ce qui est tapé aux noms des pages. */
export function sansAccents(texte) {
    return (texte ?? "").toString().normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/** Même règle que l'API : au moins 4 chiffres et rien d'autre que des séparateurs. */
export function estTelephone(texte) {
    const t = (texte ?? "").trim();
    return /^\+?[\d\s.\-/()]+$/.test(t) && t.replace(/\D/g, "").length >= 4;
}

export const GROUPES = [
    {cle: "clients", titre: "Clients", icone: "fa fa-user"},
    {cle: "prises_en_charge", titre: "Prises en charge", icone: "fa fa-wrench"},
    {cle: "factures", titre: "Factures", icone: "fa fa-file-text-o", module: "document"},
    {cle: "devis", titre: "Devis", icone: "fa fa-file-o", module: "document"},
    {cle: "prestations", titre: "Prestations", icone: "fa fa-book"},
    {cle: "produits", titre: "Produits", icone: "fa fa-shopping-cart", module: "stock"},
    {cle: "pages", titre: "Pages", icone: "fa fa-compass"},
];

/** Adresse du tableau de bord qui ouvre un résultat. */
export function lienResultat(groupe, item) {
    switch (groupe) {
        case "clients":
            return "/clients/modifier/" + item.id;
        case "prises_en_charge":
            return "/priseencharge/voir/" + item.id + "/0";
        case "factures":
            return "/factures/modifier/" + item.id + "/" + (item.status ?? "");
        case "devis":
            return "/devis/edit/" + item.id;
        case "prestations":
            return "/prestations/edit/" + item.id;
        case "produits":
            return "/stock/edit/" + item.id;
        case "pages":
            return item.path;
        default:
            return null;
    }
}

/**
 * Pages du menu dont le nom contient chaque mot tapé (« fact » → Factures, « stat app » → Statistiques appels).
 * Seules les pages sans paramètre et autorisées au compte (droits et modules) sont proposées.
 */
export function pagesCorrespondantes(routes, texte, autorisee, limite = 4) {
    const mots = sansAccents(texte).split(/\s+/).filter(Boolean);
    if (mots.length === 0) {
        return [];
    }
    const vues = new Set();
    return routes
        .filter(route => route.path && route.layout === "/admin" && !route.path.includes(":") && autorisee(route))
        .filter(route => {
            const nom = sansAccents(route.name);
            return mots.every(mot => nom.includes(mot)) && !vues.has(nom) && vues.add(nom);
        })
        .slice(0, limite)
        .map(route => ({id: route.path, titre: route.name, path: route.path}));
}

/**
 * Liste à plat des résultats, groupe par groupe, pour la navigation au clavier.
 * Les clients passent en premier (un numéro de téléphone les cherche), les pages en dernier.
 */
export function resultatsAPlat(datas, groupes = GROUPES) {
    return groupes.flatMap(groupe => (datas?.[groupe.cle] ?? []).map(item => ({groupe: groupe.cle, item})));
}

/**
 * Choix proposés dans la recherche d'une ligne de facture : chaque prestation, et pour chaque produit
 * un choix par lot en stock (une facture sort un lot précis). Une commande fournisseur n'a pas de lot :
 * le produit lui-même est proposé. Un produit sans lot en stock reste visible mais n'est pas choisissable.
 */
export function choixLignes(datas, sansLot = false) {
    const prestations = (datas?.prestations ?? []).map(p => ({
        type: "presta", cle: "s" + p.id, titre: p.fullname, detail: p.isVisible ? "" : "non publiée",
        prix: p.prix_unitaire, prestation: p,
    }));

    const produits = (datas?.produits ?? []).flatMap(p => {
        if (sansLot) {
            return [{type: "product", cle: "p" + p.id, titre: p.nom, detail: [p.ref, p.stock + " en stock"].filter(Boolean).join(" · "), prix: p.prix_vente, produit: p}];
        }
        if (!p.lots?.length) {
            return [{type: "product", cle: "p" + p.id, titre: p.nom, detail: "épuisé", prix: p.prix_vente, produit: p, indisponible: true}];
        }
        return p.lots.map(lot => ({
            type: "product", cle: "l" + lot.id, titre: p.nom,
            detail: ["lot " + lot.pid, lot.stock + " en stock"].join(" · "), prix: p.prix_vente, produit: p, lot,
        }));
    });

    return prestations.concat(produits);
}
